import { Bar } from "react-chartjs-2"
import { TooltipItem, TooltipModel } from "chart.js"
import styled from "styled-components"

import { ApiTypes } from "types"

import { useWindowResize } from "hooks/useWindowResize"

import { externalTooltipHandler } from "./CustomModesBarTooltip"
import { BarChartSettings } from "./getBarChartsSettings"
import parseFormattedValueToTime from "./parseFormattedValueToTime"
import { Title } from "./styled"

const ChartContainer = styled.div`
  height: 180px;
`

interface SystemModesBarChartProps {
  barChartSettings: BarChartSettings
  historicalData: ApiTypes.Res.MonitoringHistoricalDataBucket[]
}

const SystemModesBarChart = ({ barChartSettings, historicalData }: SystemModesBarChartProps) => {
  const ticks = barChartSettings.ticksCount
  const isLargeScreen = useWindowResize()

  const barChartsData = (
    historicalData.reduce(
      (acc, item, index) => {
        if (index % barChartSettings.intervalItemsCount === 0) {
          acc.push({
            auto: 0,
            cooling: 0,
            emergency_heat: 0,
            heating: 0,
            off: 0,
            vacation: 0,
          })
        }
        const lastItem = acc[acc.length - 1]
        lastItem.auto += item["filters#running_mode_count"].buckets.auto.doc_count
        lastItem.cooling += item["filters#running_mode_count"].buckets.cooling.doc_count
        lastItem.emergency_heat += item["filters#running_mode_count"].buckets.emergency_heat.doc_count
        lastItem.heating += item["filters#running_mode_count"].buckets.heating.doc_count
        lastItem.off += item["filters#running_mode_count"].buckets.off.doc_count
        lastItem.vacation += item["filters#running_mode_count"].buckets.vacation.doc_count

        return acc
      },
      [] as {
        auto: number
        cooling: number
        emergency_heat: number
        heating: number
        off: number
        vacation: number
      }[],
    ) || []
  ).map(item => ({
    auto: item.auto / barChartSettings.hourlyDivider,
    cooling: item.cooling / barChartSettings.hourlyDivider,
    emergency_heat: item.emergency_heat / barChartSettings.hourlyDivider,
    heating: item.heating / barChartSettings.hourlyDivider,
    off: item.off / barChartSettings.hourlyDivider,
    vacation: item.vacation / barChartSettings.hourlyDivider,
  }))

  const labels = [...Array(ticks)].map((_, index) => index + 1)
  const data = {
    labels: labels,
    datasets: [
      {
        label: "Cooling",
        backgroundColor: "#3c82f6",
        data: barChartsData.map(item => item.cooling),
        barPercentage: barChartSettings.barWidth,
        categoryPercentage: barChartSettings.barWidth,
      },
      {
        label: "Heating",
        backgroundColor: "#ef4444",
        data: barChartsData.map(item => item.heating),
        barPercentage: barChartSettings.barWidth,
        categoryPercentage: barChartSettings.barWidth,
      },
      {
        label: "Auto",
        backgroundColor: "#f59e0c",
        data: barChartsData.map(item => item.auto),
        barPercentage: barChartSettings.barWidth,
        categoryPercentage: barChartSettings.barWidth,
      },
      {
        label: "Vacation",
        backgroundColor: "#22c55d",
        data: barChartsData.map(item => item.vacation),
        barPercentage: barChartSettings.barWidth,
        categoryPercentage: barChartSettings.barWidth,
      },
      {
        label: "Em/Aux",
        backgroundColor: "#7f1c1d",
        data: barChartsData.map(item => item.emergency_heat),
        barPercentage: barChartSettings.barWidth,
        categoryPercentage: barChartSettings.barWidth,
      },
      {
        label: "Off",
        backgroundColor: "#cbd5e1",
        data: barChartsData.map(item => item.off),
        barPercentage: barChartSettings.barWidth,
        categoryPercentage: barChartSettings.barWidth,
      },
    ],
  }

  const options = {
    interaction: {
      mode: "index" as const,
    },
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: false,
      },
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
        position: "nearest" as const,
        external: externalTooltipHandler,
        callbacks: {
          label: function (this: TooltipModel<"bar">, tooltipItem: TooltipItem<"bar">) {
            return `${tooltipItem.dataset.label}: ${parseFormattedValueToTime(tooltipItem.formattedValue, barChartSettings.dataType)}`
          },
        },
      },
    },
    responsive: true,
    scales: {
      x: {
        stacked: true,
        title: {
          display: isLargeScreen,
          text: barChartSettings.labelX,
          color: "#94A3B8",
        },
        ticks: {
          color: "#94A3B8",
          // TODO: Fix ticks count for backend data
          maxTicksLimit: ticks + 1,
        },
      },
      y: {
        title: {
          display: isLargeScreen,
          text: `System modes ${barChartSettings.labelY}`,
          color: "#94A3B8",
        },
        stacked: true,
        max: barChartSettings.suggestedMax,
        ticks: {
          maxTicksLimit: 6,
          color: "#94A3B8",
        },
      },
    },
  }

  return (
    <>
      {!isLargeScreen && <Title>System modes ({barChartSettings.dataType})</Title>}
      <ChartContainer>
        <Bar data={data} options={options} />
      </ChartContainer>
    </>
  )
}

export default SystemModesBarChart
